/**
 * CompletionScreen — ScreenClose: warm gradient, "Thank you for sitting."
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 * screen-integration-007
 */
import { useState } from "react";
import styles from "./CompletionScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Props = {
  retreatName: string;
  durationLabel: string;
  dateRangeLabel: string;
  whisperText?: string;
  sessionSaved?: boolean;
  onReflect: () => void;
  onClose: () => void;
  onBell?: () => void;
  onFeedback?: () => void;
};

export function CompletionScreen({
  retreatName,
  durationLabel,
  dateRangeLabel,
  whisperText,
  onReflect,
  onClose,
  onBell,
  onFeedback,
}: Props) {
  const [bellRung, setBellRung] = useState(false);

  function handleRingBell() {
    if (bellRung) return;
    setBellRung(true);
    onBell?.();
  }

  return (
    <div className={styles.completion} data-testid={TEST_IDS.COMPLETION_SCREEN}>
      <div className={styles.centerBlock}>
        <button
          className={styles.bellRingBtn}
          onClick={handleRingBell}
          type="button"
          aria-label={bellRung ? "Bell has rung" : "Ring the closing bell"}
          data-testid={TEST_IDS.COMPLETION_BELL_BTN}
        >
          <div className={styles.dot} data-rung={bellRung} />
        </button>
        <div className={styles.eyebrow}>
          {bellRung ? "The bell has rung" : "Tap to ring the closing bell"}
        </div>
        <h1 className={styles.heading}>Thank you for sitting.</h1>
        <div className={styles.retreatMeta}>{retreatName} · {durationLabel}</div>
        <div className={styles.dateMeta}>{dateRangeLabel}</div>
        {whisperText && (
          <div className={styles.whisper}>{whisperText}</div>
        )}
      </div>

      <div className={styles.ctas}>
        {onFeedback && (
          <div className={styles.feedbackNudge}>
            <p className={styles.feedbackPrompt}>How was your retreat? We'd love to hear.</p>
            <button
              className={styles.feedbackBtn}
              onClick={onFeedback}
              type="button"
              data-testid={TEST_IDS.COMPLETION_FEEDBACK_BTN}
            >
              Share feedback
            </button>
          </div>
        )}
        <PrimaryAction onClick={onReflect} testId={TEST_IDS.COMPLETION_REFLECT_BTN}>Write a reflection</PrimaryAction>
        <SecondaryAction onClick={onClose} testId={TEST_IDS.COMPLETION_CLOSE_BTN}>Close</SecondaryAction>
      </div>
    </div>
  );
}
